// IMPORTANT : Avant de lancer l'application, exécutez les commandes suivantes :
// 1. sudo chown -R 501:20 "/Users/user/.npm"
// 2. npm install @supabase/supabase-js
// 3. Remplacez les valeurs REACT_APP_SUPABASE_URL et REACT_APP_SUPABASE_ANON_KEY dans .env
// par les valeurs de votre projet Supabase

import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import ServiceForm from './ServiceForm';
import AdminButton from '../../../components/admin/AdminButton';
import { getSupabase } from '../../../supabaseClient';
import toast from 'react-hot-toast';
import { useEffectOnce } from '../../../hooks/useEffectOnce';
import DOMPurify from 'dompurify';
import { useAuth } from '../../../contexts/AuthContext';

// Bind modal to your appElement for accessibility
Modal.setAppElement('#root');

const ServicesList = () => {
  // États du composant
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredServices, setFilteredServices] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [confirmModalIsOpen, setConfirmModalIsOpen] = useState(false);
  const [serviceToToggle, setServiceToToggle] = useState(null);
  
  // Récupérer l'utilisateur connecté
  const { user } = useAuth();
  
  // Vérifier si l'utilisateur est restreint
  const isRestrictedUser = user?.email === 'contact@cgci-paris.com';
  
  // Styles personnalisés pour la modale
  const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 1000,
      padding: 0,
      borderRadius: '0.75rem',
    }
  };

  // Récupérer les services avec leurs détails
  const fetchServicesWithDetails = async () => {
    try {
      const supabase = getSupabase();
      
      // Récupérer les services avec leurs catégories et actes
      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select(`
          *,
          categories (
            id,
            name,
            consular_acts (
              id
            )
          )
        `);
      
      if (servicesError) {
        throw servicesError;
      }
      
      // Transformer les données pour avoir le nombre de catégories et d'actes
      const enrichedServices = servicesData.map(service => ({
        ...service,
        categoriesCount: service.categories ? service.categories.length : 0,
        actsCount: service.categories
          ? service.categories.reduce((total, category) => 
              total + (category.consular_acts ? category.consular_acts.length : 0), 
              0
            ) 
          : 0
      }));
      
      setServices(enrichedServices);
      
      // Afficher un toast si aucun service n'est trouvé
      if (!enrichedServices || enrichedServices.length === 0) {
        toast.info('Aucun service trouvé');
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des services:', err);
      toast.error(`Erreur : ${err.message}`);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  // Charger les services une seule fois au montage
  useEffectOnce(() => {
    fetchServicesWithDetails();
  });

  // Filtrer les services en fonction du terme de recherche
  useEffect(() => {
    if (searchTerm) {
      const filtered = services.filter(service => 
        service.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredServices(filtered);
    } else {
      setFilteredServices(services);
    }
  }, [searchTerm, services]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Gérer l'édition d'un service
  const handleEdit = (service) => {
    setSelectedService(service);
    setModalIsOpen(true);
  };

  // Gérer le changement d'état (actif/inactif) d'un service
  const handleToggleActive = async () => {
    if (!serviceToToggle) return;
    
    const toastId = toast.loading(`${serviceToToggle.active ? 'Désactivation' : 'Activation'} du service en cours...`);
    try {
      const { error } = await getSupabase()
        .from('services')
        .update({ active: !serviceToToggle.active })
        .eq('id', serviceToToggle.id);

      if (error) throw error;
      
      // Mettre à jour la liste des services après modification
      await fetchServicesWithDetails();
      toast.success(`Le service a été ${serviceToToggle.active ? 'désactivé' : 'activé'} avec succès`, { id: toastId });
    } catch (err) {
      console.error(`Erreur lors du changement d'état du service:`, err);
      toast.error(`Erreur : ${err.message}`, { id: toastId });
    } finally {
      setConfirmModalIsOpen(false);
      setServiceToToggle(null);
    }
  };

  // Ouvrir la modale de confirmation pour le toggle
  const openToggleConfirmModal = (service) => {
    setServiceToToggle(service);
    setConfirmModalIsOpen(true);
  };

  // Fermer la modale de confirmation pour le toggle
  const closeToggleConfirmModal = () => {
    setConfirmModalIsOpen(false);
    setServiceToToggle(null);
  };

  // Gérer la suppression d'un service
  const handleDelete = async (serviceId) => {
    const toastId = toast.loading('Suppression du service...');
    try {
      const { error } = await getSupabase()
        .from('services')
        .delete()
        .eq('id', serviceId);

      if (error) throw error;
      
      // Mettre à jour la liste des services après suppression
      setServices(services.filter(service => service.id !== serviceId));
      toast.success('Service supprimé avec succès', { id: toastId });
    } catch (error) {
      console.error('Erreur lors de la suppression du service :', error);
      toast.error(`Erreur lors de la suppression : ${error.message}`, { id: toastId });
    }
  };

  // Fonction pour tronquer le texte HTML
  const truncateHtml = (html, maxLength = 200) => {
    if (!html) return '';
    // Créer un élément temporaire pour extraire le texte
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = DOMPurify.sanitize(html);
    let text = tempDiv.textContent || tempDiv.innerText || '';
    // Tronquer le texte
    if (text.length > maxLength) {
      text = text.substr(0, maxLength) + '...';
    }
    return text;
  };

  // Rendu du contenu du tableau
  const renderContent = () => {
    // État de chargement
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
        </div>
      );
    }

    // Aucun service trouvé
    if (filteredServices.length === 0) {
      return (
        <div className="text-center py-10 text-gray-500">
          <p className="text-lg">
            {searchTerm 
              ? `Aucun service ne correspond à "${searchTerm}"` 
              : 'Aucun service n\'a été trouvé'}
          </p>
        </div>
      );
    }

    // Rendu des cartes de services
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-0">
        {filteredServices.map((service) => (
          <div 
            key={service.id} 
            className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 flex flex-col h-full"
          >
            {/* Icône et titre */}
            <div className="p-6 bg-[#e88c00]/10 border-b border-gray-200">
              <div className="flex items-center space-x-4">
                <div className="bg-primary p-3 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  {service.name}
                </h3>
              </div>
            </div>
            
            {/* Description */}
            <div className="p-6 flex-grow">
              <p className="text-gray-600 mb-6">
              {truncateHtml(service.description)}
              </p>
              
              {/* Compteurs */}
              <div className="flex justify-between mb-2 text-sm">
                <div className="flex items-center bg-gray-100 rounded-full p-2 space-x-2">
                  <span className="font-medium text-gray-700">catégorie(s)</span>
                  <span className="ml-2 bg-secondary text-white font-bold px-2 py-1 rounded-full">
                    {service.categoriesCount}
                  </span>
                </div>
                <div className="flex items-center bg-gray-100 rounded-full p-2 space-x-2">
                  <span className="font-medium text-gray-700">acte(s)</span>
                  <span className="ml-2 bg-secondary text-white font-bold px-2 py-1 rounded-full">
                    {service.actsCount}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Boutons d'action */}
            <div className="bg-gray-100 px-6 py-3 flex justify-between items-center border-t border-gray-200 mt-auto">
              {!isRestrictedUser ? (
                <>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => openToggleConfirmModal(service)}
                      className={`flex items-center px-4 py-2 ${service.active ? 'bg-green-600' : 'bg-gray-500'} text-white rounded-md hover:opacity-90 transition-colors`}
                      title={service.active ? 'Désactiver le service' : 'Activer le service'}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={service.active ? "M5 13l4 4L19 7" : "M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"} />
                      </svg>
                      {service.active ? 'Actif' : 'Inactif'}
                    </button>
                    <button 
                      onClick={() => handleEdit(service)}
                      className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Modifier
                    </button>
                  </div>
                  <button 
                    onClick={() => handleDelete(service.id)}
                    className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </>
              ) : (
                <div className="w-full text-center text-gray-500 text-sm">
                  {service.active ? 'Service actif' : 'Service inactif'}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Fonction pour ouvrir la modale
  const openModal = () => {
    console.log('Avant ouverture - État actuel:', modalIsOpen);
    // Utiliser une fonction de mise à jour avec le callback pour s'assurer de la mise à jour
    setModalIsOpen(prevState => {
      console.log('Mise à jour de l\'état de la modale:', !prevState);
      return true;
    });
  };

  // Fonction pour fermer la modale
  const closeModal = () => {
    console.log('Avant fermeture - État actuel:', modalIsOpen);
    // Utiliser une fonction de mise à jour avec le callback
    setModalIsOpen(prevState => {
      console.log('Mise à jour de l\'état de la modale:', !prevState);
      return false;
    });
  };

  // Effet pour suivre les changements de l'état de la modale
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    console.log('🔍 État de la modale a changé:', modalIsOpen);
    console.log('🔍 Styles de la modale:', customStyles.content);
  }, [modalIsOpen]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Gestion des services</h2>
        {!isRestrictedUser && (
          <AdminButton 
            type="primary" 
            onClick={() => {
              console.log('Bouton "Nouveau service" cliqué');
              setSelectedService(null);
              openModal();
            }}
            icon="add"
          >
            Nouveau service
          </AdminButton>
        )}
      </div>
      
      <div className="mb-6">
        <input 
          type="text" 
          placeholder="Rechercher un service" 
          value={searchTerm}
          onChange={handleSearchChange}
          className="tracking-input rounded-r-none flex-grow"
        />
      </div>
      
      {/* Services Table or Empty State */}
      {renderContent()}
      
      {/* Modal pour le formulaire de service avec react-modal */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Formulaire de service"
        overlayClassName="ReactModal__Overlay"
        className="max-w-2xl w-full max-h-[90vh] bg-white rounded-xl shadow-lg overflow-hidden flex flex-col"
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        ariaHideApp={false}
      >
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-xl font-bold text-gray-900">
            {selectedService ? 'Modifier un service' : 'Nouveau service'}
          </h3>
          <button 
            type="button"
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <ServiceForm 
            isModal={true} 
            navigate={() => {}} 
            onSuccess={(newService) => {
              closeModal();
              fetchServicesWithDetails();
              console.log('Nouveau service créé :', newService);
            }}
            id={selectedService?.id}
          />
        </div>
      </Modal>

      {/* Modale de confirmation pour activer/désactiver un service */}
      <Modal
        isOpen={confirmModalIsOpen}
        onRequestClose={closeToggleConfirmModal}
        contentLabel="Confirmation de changement d'état"
        overlayClassName="ReactModal__Overlay"
        className="max-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden flex flex-col"
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        ariaHideApp={false}
      >
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-xl font-bold text-gray-900">
            Confirmation
          </h3>
          <button 
            type="button"
            onClick={closeToggleConfirmModal}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="p-6">
          <p className="text-gray-700 mb-6">
            {serviceToToggle?.active 
              ? `Êtes-vous sûr de vouloir désactiver le service "${serviceToToggle?.name}" ?` 
              : `Êtes-vous sûr de vouloir activer le service "${serviceToToggle?.name}" ?`}
          </p>
          <div className="flex justify-end space-x-4">
            <button
              onClick={closeToggleConfirmModal}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleToggleActive}
              className={`px-4 py-2 ${serviceToToggle?.active ? 'bg-orange-500' : 'bg-green-600'} text-white rounded-md hover:opacity-90 transition-colors`}
            >
              {serviceToToggle?.active ? 'Désactiver' : 'Activer'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ServicesList;
